import { ConfiguredCarCarCode } from '@oneaudi/onegraph-client';
import { Icon, IconProps, Text, TextButton } from '@oneaudi/unified-web-components';
import { useAtomValue } from 'jotai';
import { forwardRef, Ref, useCallback } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { NextBestActionType, useMarketConfig } from '~/app/market-config';
import { hyphenateCarlineId } from '~/app/market-config/hyphenate-carline';
import { PoaTrigger } from '~/app/pages/summary/components/next-best-actions/components/poa-trigger';
import { NextBestActionButtonStyle } from '~/app/pages/summary/components/next-best-actions/styles/next-best-actions.style';
import { getResolvedUrl } from '~/app/utils/url-builder';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useGetAudiCode } from '~/graphql/hooks/use-get-audicode';
import { state } from '~/storage';

export interface NextBestActionProps {
	nba: NextBestActionType;
	trackNbaClick: (nba: NextBestActionType, targetUrl: string, elementName: string) => void;
}

export const SecondaryNextBestAction = forwardRef<HTMLButtonElement, NextBestActionProps>(
	({ nba, trackNbaClick }, ref) => {
		const { icon, labelText, description, url, trackingName, target } = nba;
		const { language, config } = useMarketConfig();
		const { catalog } = useConfigurator();
		const defaultProduct = useAtomValue(state.defaultFinancialProduct);
		const { getAudiCode } = useGetAudiCode();
		const { i18n } = useI18n();
		const targetTextReaderOnly = !target || target === '_blank' ? i18n('ngc.a11y.openInNewTab') : '';

		const onClickHandler = useCallback(
			(audiCode: ConfiguredCarCarCode) => {
				if (!catalog.carInfo?.carline || !catalog.trimlines || !audiCode || Object.keys(audiCode).length === 0)
					return;
				const driveTrain = catalog.trimlines.flatMap((t) => t.driveTrains).find((item) => item.status.selected);
				if (!driveTrain) return;

				trackNbaClick(nba, url, 'text link');

				if (target === '_poa' && nba.botID) {
					PoaTrigger(nba.botID, audiCode);
					return;
				}

				const resolvedUrl = getResolvedUrl(
					url,
					audiCode.id,
					language,
					encodeURIComponent(catalog.carInfo.carline),
					config.carlineId,
					hyphenateCarlineId(config.carlineId),
					catalog.carInfo?.carIdentifier.model.year.toString(),
					config.financingInput?.product ?? defaultProduct,
					encodeURIComponent(driveTrain.name),
				);

				window.open(resolvedUrl, target || '_blank');
			},
			[
				catalog.carInfo?.carline,
				catalog.carInfo?.carIdentifier.model.year,
				catalog.trimlines,
				trackNbaClick,
				JSON.stringify(nba),
				url,
				target,
				language,
				config.carlineId,
				config.financingInput?.product,
				defaultProduct,
			],
		);

		return (
			<NextBestActionButtonStyle
				id={trackingName}
				ref={ref}
				onClick={() => getAudiCode(onClickHandler)}
				aria-label={targetTextReaderOnly ? `${labelText}. ${targetTextReaderOnly}` : labelText}>
				<div className={'left'}>
					<Icon name={icon as IconProps['name']} size={'s'} />
				</div>
				<div className={'right'}>
					<Text tag="p" variant={{ type: 'copy', order: '1', style: 'normal' }}>
						{labelText}
					</Text>
					<Text tag="p" className={'description'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
						{description}
					</Text>
				</div>
			</NextBestActionButtonStyle>
		);
	},
);

SecondaryNextBestAction.displayName = 'SecondaryNextBestAction';

export const PrimaryNextBestAction = forwardRef<HTMLAnchorElement, NextBestActionProps>(
	({ nba, trackNbaClick }, ref) => {
		const { icon, labelText, trackingName, target, botID, url } = nba;
		const { language, config } = useMarketConfig();
		const { catalog } = useConfigurator();
		const defaultProduct = useAtomValue(state.defaultFinancialProduct);
		const { getAudiCode, isLoadingAudiCode, shouldDisableButton } = useGetAudiCode();
		const { i18n } = useI18n();
		const targetTextReaderOnly = target === '_blank' ? i18n('ngc.a11y.openInNewTab') : '';

		const generateTargetUrl = useCallback(
			(audiCode: ConfiguredCarCarCode) => {
				if (!catalog.carInfo?.carline || !catalog.trimlines) return null;
				const driveTrain = catalog.trimlines.flatMap((t) => t.driveTrains).find((item) => item.status.selected);
				if (!driveTrain) return null;

				return getResolvedUrl(
					url || audiCode.urls?.pdf || '',
					audiCode.id,
					language,
					encodeURIComponent(catalog.carInfo.carline),
					config.carlineId,
					hyphenateCarlineId(config.carlineId),
					catalog.carInfo?.carIdentifier.model.year.toString(),
					config.financingInput?.product ?? defaultProduct,
					encodeURIComponent(driveTrain.name),
				);
			},
			[
				catalog.carInfo?.carline,
				catalog.carInfo?.carIdentifier.model.year,
				catalog.trimlines,
				language,
				config.carlineId,
				config.financingInput?.product,
				defaultProduct,
			],
		);

		const onClickHandler = useCallback(
			(audiCode: ConfiguredCarCarCode) => {
				if (!audiCode || Object.keys(audiCode).length === 0) return;
				const targetUrl = generateTargetUrl(audiCode);
				trackNbaClick(nba, targetUrl ?? '', 'button');
				if (target === '_poa' && botID) {
					PoaTrigger(botID, audiCode);
				} else {
					window.open(targetUrl ?? '#', target);
				}
			},
			[botID, nba, target, generateTargetUrl, trackNbaClick],
		);

		return (
			<TextButton
				ref={ref as Ref<HTMLButtonElement>}
				id={trackingName}
				aria-label={targetTextReaderOnly ? `${labelText}. ${targetTextReaderOnly}` : labelText}
				variant={'secondary'}
				size={'md'}
				onClick={() => getAudiCode(onClickHandler)}
				icon={icon as IconProps['name']}
				isLoading={isLoadingAudiCode}
				disabled={shouldDisableButton}
				iconPosition={'leading'}>
				{labelText}
			</TextButton>
		);
	},
);

PrimaryNextBestAction.displayName = 'PrimaryNextBestAction';
